import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { PortalAdminService } from 'src/app/services/portal-admin.service';
import { KundeKortService } from 'src/app/services/kunde-kort.service';
import { MembershipV2Service } from 'src/app/services/membership-v2.service';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/shared/notification.service';

// Super-admin page for granting a CUSTOMER (kundekort) access to the
// self-service portal, where they manage their own employees instead of
// emailing us to do it.
//
// Access is granted per kundekort because that is what a group — and therefore
// an employee — hangs off. A customer with no group linked to them would sign
// in to an empty portal, so the group count is shown next to every customer
// here, and the Customer Mapping page is where a missing link gets fixed.
@Component({
  selector: 'portal-access',
  templateUrl: './portal-access.component.html',
  styleUrls: ['./portal-access.component.css']
})
export class PortalAccessComponent implements OnInit, OnDestroy {

  _isSuperAdmin = false;
  loading = true;
  error = '';

  users: any[] = [];
  customers: any[] = [];
  groupCountByCustomer: { [customerId: string]: number } = {};

  search = '';
  customerSearch = '';

  // One form serves create and edit. `editingUid` is null when creating —
  // on edit the email is fixed (it is the Firebase Auth identity) and only
  // name, role and the customer list can change.
  showForm = false;
  editingUid: string | null = null;
  form: { email: string, name: string, role: string, customerIds: string[] } =
    { email: '', name: '', role: 'manager', customerIds: [] };
  saving = false;
  busy: { [uid: string]: boolean } = {};

  readonly roles = [
    { value: 'owner', label: 'Owner', hint: 'Manage employees and invite colleagues to the portal' },
    { value: 'manager', label: 'Manager', hint: 'Manage employees' },
    { value: 'viewer', label: 'Viewer', hint: 'Read-only — see employees and purchases' }
  ];

  private subs: Subscription[] = [];

  constructor(
    private svc: PortalAdminService,
    private kundeKort: KundeKortService,
    private membership: MembershipV2Service,
    private auth: AuthService,
    private notifier: NotificationService
  ) { }

  async ngOnInit() {
    const user: any = await this.auth.getUserPromise();
    this._isSuperAdmin = this.auth.isSuperAdmin(user);
    if (!this._isSuperAdmin) { this.loading = false; return; }

    this.subs.push(this.kundeKort.getCustomers().subscribe((list: any[]) => {
      this.customers = (list || [])
        .map(c => ({ customerId: c.kundekortId, name: c.name || c.legalName || c.kundekortId, orgId: c.orgId || '' }))
        .sort((a, b) => a.name.localeCompare(b.name, 'nb'));
    }));

    // Group counts come from the same root `groups` collection the portal API
    // scopes on, so "0 groups" here means exactly "this customer would see no
    // employees in the portal".
    this.subs.push(this.membership.getAllGroupsLive$().subscribe((groups: any[]) => {
      const counts: { [id: string]: number } = {};
      (groups || []).forEach(g => { if (g.customerId) counts[g.customerId] = (counts[g.customerId] || 0) + 1; });
      this.groupCountByCustomer = counts;
    }));

    await this.reload();
  }

  ngOnDestroy() { this.subs.forEach(s => s.unsubscribe()); }

  async reload() {
    this.loading = true;
    this.error = '';
    try {
      this.users = await this.svc.listUsers();
    } catch (err: any) {
      this.error = (err && err.error && err.error.message) || 'Could not load portal accounts.';
    }
    this.loading = false;
  }

  customerName(id: string): string {
    const c = this.customers.find(x => x.customerId === id);
    return c ? c.name : id;
  }

  customerNames(ids: string[]): string {
    return (ids || []).map(id => this.customerName(id)).join(', ');
  }

  // A portal account whose customers have no groups is the one failure mode
  // that looks like a broken app to the customer, so flag it in the list.
  hasNoGroups(user: any): boolean {
    return (user.customerIds || []).every((id: string) => !this.groupCountByCustomer[id]);
  }

  get filteredUsers(): any[] {
    const term = this.search.trim().toLowerCase();
    if (!term) { return this.users; }
    return this.users.filter(u =>
      (u.email || '').toLowerCase().includes(term) ||
      (u.name || '').toLowerCase().includes(term) ||
      this.customerNames(u.customerIds).toLowerCase().includes(term));
  }

  get filteredCustomers(): any[] {
    const term = this.customerSearch.trim().toLowerCase();
    if (!term) { return this.customers; }
    return this.customers.filter(c =>
      c.name.toLowerCase().includes(term) || (c.orgId || '').includes(term));
  }

  openForm() {
    this.editingUid = null;
    this.form = { email: '', name: '', role: 'manager', customerIds: [] };
    this.customerSearch = '';
    this.showForm = true;
  }

  // Edit an existing account: change which customers they manage, their role,
  // or their name. Customers are edited here rather than in Firestore because
  // changing them must also rewrite the account's custom claim — the endpoint
  // does both, so the API and the security rules never disagree about who this
  // person is.
  editUser(user: any) {
    this.editingUid = user.uid;
    this.form = {
      email: user.email,
      name: user.name || '',
      role: user.role || 'viewer',
      customerIds: [...(user.customerIds || [])]
    };
    this.customerSearch = '';
    this.showForm = true;
  }

  cancelForm() {
    this.showForm = false;
    this.editingUid = null;
  }

  async save() {
    if (this.editingUid) { return this.saveEdit(); }
    return this.create();
  }

  private async saveEdit() {
    if (!this.form.customerIds.length) {
      this.notifier.showNotification('error', 'A portal user needs at least one customer');
      return;
    }
    this.saving = true;
    try {
      await this.svc.updateUser(this.editingUid, {
        name: this.form.name,
        role: this.form.role,
        customerIds: this.form.customerIds
      });
      this.notifier.showNotification('success', 'Portal access updated');
      this.showForm = false;
      this.editingUid = null;
      await this.reload();
    } catch (err: any) {
      this.notifier.showNotification('error', (err && err.error && err.error.message) || 'Could not update portal access');
    }
    this.saving = false;
  }

  private async create() {
    const email = this.form.email.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      this.notifier.showNotification('error', 'A valid email address is required');
      return;
    }
    if (!this.form.customerIds.length) {
      this.notifier.showNotification('error', 'Pick at least one customer');
      return;
    }
    this.saving = true;
    try {
      const res: any = await this.svc.createUser({ ...this.form, email });
      if (res && res.success) {
        this.notifier.showNotification('success', res.invited
          ? 'Portal account created — invite email sent'
          : 'Portal account created, but the invite email could not be sent. Use "Resend invite".');
        this.showForm = false;
        await this.reload();
      } else {
        this.notifier.showNotification('error', (res && res.message) || 'Could not create the portal account');
      }
    } catch (err: any) {
      this.notifier.showNotification('error', (err && err.error && err.error.message) || 'Could not create the portal account');
    }
    this.saving = false;
  }

  async setStatus(user: any, status: boolean) {
    this.busy[user.uid] = true;
    try {
      await this.svc.updateUser(user.uid, { status });
      user.status = status;
      this.notifier.showNotification('success', status ? 'Access restored' : 'Access revoked');
    } catch (err: any) {
      this.notifier.showNotification('error', (err && err.error && err.error.message) || 'Could not change access');
    }
    this.busy[user.uid] = false;
  }

  async setRole(user: any, role: string) {
    this.busy[user.uid] = true;
    try {
      await this.svc.updateUser(user.uid, { role });
      user.role = role;
      this.notifier.showNotification('success', 'Role updated');
    } catch (err: any) {
      this.notifier.showNotification('error', (err && err.error && err.error.message) || 'Could not change the role');
    }
    this.busy[user.uid] = false;
  }

  async resendInvite(user: any) {
    this.busy[user.uid] = true;
    try {
      const res: any = await this.svc.resendInvite(user.uid);
      if (res && res.success) { this.notifier.showNotification('success', 'Invite sent'); }
      else { this.notifier.showNotification('error', (res && res.message) || 'Could not send the invite'); }
    } catch (err: any) {
      this.notifier.showNotification('error', (err && err.error && err.error.message) || 'Could not send the invite');
    }
    this.busy[user.uid] = false;
  }

  toggleCustomer(customerId: string) {
    const i = this.form.customerIds.indexOf(customerId);
    if (i >= 0) { this.form.customerIds.splice(i, 1); }
    else { this.form.customerIds.push(customerId); }
  }

  isPicked(customerId: string): boolean {
    return this.form.customerIds.includes(customerId);
  }

  roleLabel(role: string): string {
    const r = this.roles.find(x => x.value === role);
    return r ? r.label : role;
  }
}
